/**
 *
 * App
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 */

import fetchIntercept from 'fetch-intercept';
import { Fragment, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Helmet } from 'react-helmet';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components/macro';
import tw from 'tailwind.macro';
import { isFavoritePassengersEnabled } from '../../common/feature-flags';
import { AppBottomNavigation } from '../../components/AppBottomNavigation';
import AppUpdateWall from '../../components/AppUpdateWall';
import ErrorMessage from '../../components/ErrorMessage';
import Footer from '../../components/Footer';
import { Maintenance } from '../../components/Maintenance';
import { checkMaintenance } from '../../components/Maintenance/utils';
import Navbar from '../../components/Navbar';
import PromoCodeBanner from '../../components/PromoCodeBanner';
import TranslationBanner from '../../components/TranslationBanner';
import { AdminContext } from '../../contexts/Admin';
import GlobalStyle from '../../global-styles';
import { isCrawler } from '../../helpers';
import { isOutdatedApp } from '../../helpers/app';
import {
  HTTP_STATUS_CODES,
  PAYLINE_CSS_URL,
  PAYLINE_JS_URL,
} from '../../helpers/constants';
import { DEFAULT_LOCALE } from '../../i18n';
import {
  getLocaleFromPathname,
  isIndexable,
  localized,
  stripLocale,
} from '../../utils/i18nUrls';
import About from '../About/Lazy';
import AccountProfile from '../AccountProfile/Lazy';
import Admin from '../Admin/Lazy';
import ApiStatus from '../Admin/View/Panel/ApiStatus/Lazy';
import AdminMarketing from '../AdminMarketing/Lazy';
import AdminOrderEdit from '../AdminOrderEdit/Lazy';
import AdminPresales from '../AdminPresales/Lazy';
import AdminPromoCodes from '../AdminPromoCodes/Lazy';
import AncillaryPurchase from '../AncillaryPurchase/Lazy';
import BuyDiscountCard from '../BuyDiscountCard/Lazy';
import City from '../City/Lazy';
import Company from '../Company/Lazy';
import Confirmation from '../Confirmation/Lazy';
import Faq from '../Faq/Lazy';
import FeatureFlags from '../FeatureFlags/Lazy';
import GiftCardCreate from '../GiftCard/LazyCreate';
import GiftCardDelivery from '../GiftCard/LazyDelivery';
import GiftCardLanding from '../GiftCard/LazyLanding';
import GiftCardRedeem from '../GiftCard/LazyRedeem';
import GiftPotCreate from '../GiftPot/LazyCreate';
import GiftPotOccasion from '../GiftPot/LazyOccasion';
import GiftPotOrganizer from '../GiftPot/LazyOrganizer';
import GiftPotPage from '../GiftPot/LazyPage';
import GroupTravel from '../GroupTravel/Lazy';
import HomePage from '../HomePage/Lazy';
import HowItWorks from '../howItWorks/Lazy';
import { makeSelectLocale } from '../LanguageProvider/selectors';
import LegalMention from '../LegalMention/Lazy';
import Login from '../Login/Lazy';
import Mountain from '../Mountain/Lazy';
import NotFoundPage from '../NotFoundPage/Lazy';
import Orders from '../Orders/Lazy';
import Partners from '../Partners/Lazy';
import PassengersInfo from '../PassengersInfo/Lazy';
import Payment from '../Payment/Lazy';
import PaymentLink from '../PaymentLink/Lazy';
import PaymentLinkConfirmation from '../PaymentLink/paymentLinkConfirmation';
import Privacy from '../Privacy/Lazy';
import CompaniesConfiguration from '../ProvidersConfiguration/Lazy';
import RedirectPage from '../Redirect/Lazy';
import RedirectAppPage from '../RedirectApp/Lazy';
import Referral from '../Referral';
import Results from '../Results/Lazy';
import SalesOpeningConfigurationList from '../SalesOpening/configurationList/Lazy';
import UpdateSalesOpeningConfiguration from '../SalesOpening/updateConfiguration/Lazy';
import Search from '../Search/Lazy';
import Support from '../Support';
import SupportV2 from '../SupportV2/Lazy';
import TallyEmbed from '../TallyEmbed/Lazy';
import TermsOfSales from '../TermsOfSales/Lazy';
import VerifyMagicLinK from '../VerifyMagicLink/Lazy';
import {
  GIFT_CARD_OCCASION_SLUGS,
  GIFT_POT_OCCASION_SLUGS,
  META_DEFAULT_IMAGE,
  NAVIGATION,
  TITLE_SUFFIX,
} from './constants';
import { appMessages } from './messages';

const MainContainer = styled.div`
  ${tw`relative  flex flex-col flex-auto
    h-full w-full
    my-0 mx-auto
    bg-white`};
  max-width: 1440px;
`;

export const App = ({
  intl,
  locale = DEFAULT_LOCALE,
}: {
  intl: any;
  locale?: string;
}) => {
  const defaultTitle = intl.formatMessage(appMessages.defaultTitle);
  const defaultDescription = intl.formatMessage(appMessages.defaultDescription);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [isSystemUnderMaintenance, setIsSystemUnderMaintenance] =
    useState(false);

  /**
   * @description Defines a global function that represents if the mobile virtual keyboard is open
   * @returns {void}
   */
  useEffect(() => {
    window['tictactrip'] = {
      setIsKeyboardOpen,
    };
  }, []);

  useEffect(() => {
    const unregister = fetchIntercept.register({
      response: function (response) {
        switch (response.status) {
          case HTTP_STATUS_CODES.SERVICE_UNAVAILABLE: {
            setIsSystemUnderMaintenance(true);
            break;
          }
          default: {
            break;
          }
        }

        return response;
      },
    });

    // Check if the API is under maintenance
    (async () => {
      const maintenance = await checkMaintenance();

      setIsSystemUnderMaintenance(maintenance);
    })();

    return () => {
      unregister();
    };
  }, []);

  // An app too old to mint a session would fail on its first API call: it is
  // sent to the store instead of through a broken site.
  if (isOutdatedApp(navigator.userAgent)) {
    return (
      <Fragment>
        <AppUpdateWall />
        <GlobalStyle />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <ErrorBoundary fallbackRender={ErrorMessage}>
        <Helmet
          htmlAttributes={{ lang: locale }}
          titleTemplate={`%s${TITLE_SUFFIX}`}
        >
          <title>{defaultTitle}</title>
          <meta content={defaultDescription} name="description" />
          <meta content="#353746" name="theme-color" />
          <meta content="website" property="og:type" />
          <meta content={defaultTitle + TITLE_SUFFIX} property="og:title" />
          <meta content={defaultDescription} property="og:description" />
          <meta content={META_DEFAULT_IMAGE} property="og:image" />
          {/* A page is noindexed in a language it is not opened to yet
              (follow: internal links stay crawlable either way). Opening is per
              page, not per language — see src/other/i18nIndexablePaths.json. */}
          {!isIndexable(
            stripLocale(window.location.pathname),
            getLocaleFromPathname(window.location.pathname),
          ) && <meta content="noindex, follow" name="robots" />}
          {!isCrawler(navigator.userAgent) && (
            <script defer src={PAYLINE_JS_URL} />
          )}
          {!isCrawler(navigator.userAgent) && (
            <link href={PAYLINE_CSS_URL} rel="stylesheet" />
          )}
        </Helmet>

        <Fragment>
          {isSystemUnderMaintenance &&
            !window.location.pathname.includes(NAVIGATION.ADMIN) && (
              <Maintenance
                setIsSystemUnderMaintenance={setIsSystemUnderMaintenance}
              />
            )}

          <Navbar />

          <PromoCodeBanner />

          <TranslationBanner />

          <MainContainer>
            <ErrorBoundary fallbackRender={ErrorMessage}>
              <Switch>
                {/* French is canonical at the unprefixed URL. nginx 301s /fr/*
                    in production; this client-side net covers dev/staging. */}
                <Route
                  path="/fr"
                  render={({ location }) => (
                    <Redirect
                      to={{
                        ...location,
                        pathname:
                          location.pathname.replace(/^\/fr(?=\/|$)/, '') || '/',
                      }}
                    />
                  )}
                />
                <Route
                  component={HomePage}
                  exact
                  path={localized(NAVIGATION.HOME)}
                />
                <Redirect
                  exact
                  from={
                    localized(`${NAVIGATION.RESULTS}/:id/outward`) as string
                  }
                  to={NAVIGATION.HOME}
                />
                <Route
                  path={localized(NAVIGATION.RESULTS)}
                  render={(props) => <Results {...props} />}
                />
                <Route
                  component={RedirectAppPage}
                  exact
                  path={localized(NAVIGATION.REDIRECT_APP)}
                />
                <Route
                  path={localized(NAVIGATION.REDIRECT)}
                  render={(props) => <RedirectPage {...props} />}
                />

                <Route
                  component={PassengersInfo}
                  exact
                  path={localized(`${NAVIGATION.INFO}/:cartId`)}
                />
                <Route
                  component={Payment}
                  exact
                  path={localized(NAVIGATION.PAYMENT)}
                />
                <Route
                  component={Payment}
                  exact
                  path={localized(`${NAVIGATION.PAYMENT}/:cartId`)}
                />
                <Route
                  exact
                  path={localized(`${NAVIGATION.CONFIRMATION}/:orderId`)}
                  render={(props) => (
                    <AdminContext.Consumer>
                      {(admin) => <Confirmation {...props} admin={admin} />}
                    </AdminContext.Consumer>
                  )}
                />
                <Route
                  component={AncillaryPurchase}
                  exact
                  path={localized(
                    `${NAVIGATION.CONFIRMATION}/:orderId/options`,
                  )}
                />
                <Route
                  component={Partners}
                  exact
                  path={localized(NAVIGATION.PARTNERS)}
                />
                <Route
                  component={LegalMention}
                  exact
                  path={localized(NAVIGATION.LEGAL)}
                />
                <Route
                  component={TermsOfSales}
                  exact
                  path={localized(NAVIGATION.TERMS_OF_SALE)}
                />
                <Route
                  component={About}
                  exact
                  path={localized(NAVIGATION.ABOUT)}
                />
                <Route
                  component={GroupTravel}
                  exact
                  path={localized(NAVIGATION.GROUP_TRAVEL)}
                />
                <Route
                  component={HowItWorks}
                  exact
                  path={localized(NAVIGATION.HOW_IT_WORKS)}
                />
                <Route component={Faq} exact path={localized(NAVIGATION.FAQ)} />
                <Route
                  component={Faq}
                  exact
                  path={localized(`${NAVIGATION.FAQ}/:pageId`)}
                />
                <Route
                  component={Privacy}
                  exact
                  path={localized(NAVIGATION.PRIVACY)}
                />
                <Route
                  component={City}
                  path={localized(`${NAVIGATION.CITY}/:id`)}
                />
                <Route
                  component={Company}
                  path={localized(`${NAVIGATION.COMPANY}/:id`)}
                />
                <Route
                  path={localized(
                    `${NAVIGATION.SEARCH}/:origin/:destination/:pageType?`,
                  )}
                  render={(props) => (
                    <AdminContext.Consumer>
                      {(admin) => <Search {...props} admin={admin} />}
                    </AdminContext.Consumer>
                  )}
                />
                <Route
                  component={Mountain}
                  exact
                  path={localized(`${NAVIGATION.MOUNTAIN}`)}
                />
                <Route
                  component={Mountain}
                  path={localized(`${NAVIGATION.MOUNTAIN}/:direction/:region`)}
                />
                <Redirect
                  exact
                  from={localized(NAVIGATION.STATIONS) as string}
                  to={NAVIGATION.HOME}
                />
                <Redirect
                  from={localized(`${NAVIGATION.STATIONS}/*-:gpuid`) as string}
                  to={NAVIGATION.HOME}
                />
                <Route
                  exact
                  path={localized(NAVIGATION.ADMIN)}
                  render={(props) => <Admin {...props} />}
                />
                <Route
                  exact
                  path={localized(
                    `${NAVIGATION.ADMIN}${NAVIGATION.WIDGET_BOOKING}`,
                  )}
                  render={(props) => <Admin {...props} />}
                />

                <Route
                  component={SalesOpeningConfigurationList}
                  exact
                  path={localized(
                    `${NAVIGATION.ADMIN_SALES_OPENING_CONFIGURATION}`,
                  )}
                />

                <Route
                  component={UpdateSalesOpeningConfiguration}
                  exact
                  path={localized(
                    `${NAVIGATION.ADMIN_SALES_OPENING_CONFIGURATION}/:id`,
                  )}
                />

                <Route
                  component={AdminOrderEdit}
                  exact
                  path={localized(`${NAVIGATION.ADMIN_ORDERS}/:orderId?`)}
                />

                <Route
                  component={AdminPresales}
                  exact
                  path={localized(NAVIGATION.ADMIN_PRESALES)}
                />

                <Route
                  component={AdminPromoCodes}
                  exact
                  path={localized(NAVIGATION.ADMIN_PROMO_CODES)}
                />

                <Route
                  component={AdminMarketing}
                  exact
                  path={localized(NAVIGATION.ADMIN_MARKETING)}
                />

                <Route
                  component={VerifyMagicLinK}
                  path={localized(NAVIGATION.VERIFY_MAGIC_LINK)}
                />
                <Route component={Login} path={localized(NAVIGATION.LOGIN)} />

                <Route
                  component={PaymentLink}
                  path={localized(
                    `${NAVIGATION.ADMIN}${NAVIGATION.PAYMENT_LINK}`,
                  )}
                />
                <Route
                  component={PaymentLinkConfirmation}
                  path={localized(`${NAVIGATION.PAYMENT_LINK}`)}
                />
                <Route component={Orders} path={localized(NAVIGATION.ORDERS)} />
                <Route
                  component={CompaniesConfiguration}
                  path={localized(
                    `${NAVIGATION.ADMIN}${NAVIGATION.PROVIDERS_CONFIGURATION}`,
                  )}
                />
                <Route
                  component={Support}
                  path={localized(`${NAVIGATION.SUPPORT}`)}
                />
                <Route
                  component={SupportV2}
                  path={localized(NAVIGATION.SUPPORT_V2)}
                />
                <Route
                  component={ApiStatus}
                  path={localized(`${NAVIGATION.ADMIN}${NAVIGATION.STATUS}`)}
                />
                <Route
                  component={FeatureFlags}
                  path={localized(
                    `${NAVIGATION.ADMIN}${NAVIGATION.FEATURE_FLAGS}`,
                  )}
                />
                <Route
                  component={GiftCardLanding}
                  exact
                  path={localized(NAVIGATION.GIFT_CARD)}
                />

                <Route
                  component={GiftCardCreate}
                  exact
                  path={localized(NAVIGATION.GIFT_CARD_CREATE)}
                />

                <Route
                  component={GiftCardRedeem}
                  exact
                  path={localized(NAVIGATION.GIFT_CARD_REDEEM)}
                />

                <Route
                  component={GiftPotCreate}
                  exact
                  path={localized(NAVIGATION.GIFT_POT)}
                />

                <Route
                  component={Referral}
                  exact
                  path={localized(`${NAVIGATION.REFERRAL}/:code`)}
                />

                <Route
                  component={GiftPotOccasion}
                  exact
                  path={localized(
                    `${
                      NAVIGATION.GIFT_POT
                    }/:occasion(${GIFT_POT_OCCASION_SLUGS.join('|')})`,
                  )}
                />

                <Route
                  component={GiftPotOrganizer}
                  exact
                  path={localized(`${NAVIGATION.GIFT_POT}/:slug/suivi`)}
                />

                <Route
                  component={GiftPotPage}
                  exact
                  path={localized(`${NAVIGATION.GIFT_POT}/:slug`)}
                />

                <Route
                  component={GiftCardDelivery}
                  exact
                  path={localized(NAVIGATION.GIFT_CARD_DELIVERY)}
                />

                <Route
                  component={GiftCardLanding}
                  exact
                  path={localized(
                    `${
                      NAVIGATION.GIFT_CARD
                    }/:occasion(${GIFT_CARD_OCCASION_SLUGS.join('|')})`,
                  )}
                />

                <Route
                  component={BuyDiscountCard}
                  path={localized(NAVIGATION.BUY_A_DISCOUNT_CARD)}
                />
                <Route
                  path={localized(NAVIGATION.GIVE_YOUR_FEEDBACK)}
                  render={() => <TallyEmbed formId={'3qVMoY'} />}
                />

                {isFavoritePassengersEnabled() && (
                  <Route
                    component={AccountProfile}
                    path={localized(NAVIGATION.ACCOUNT_PROFILE)}
                  />
                )}

                <Route component={NotFoundPage} path="" />
              </Switch>
            </ErrorBoundary>
          </MainContainer>

          <AppBottomNavigation isKeyboardOpen={isKeyboardOpen} />

          <Footer />
        </Fragment>
      </ErrorBoundary>
      <GlobalStyle />
    </Fragment>
  );
};

const mapStateToProps = createStructuredSelector({
  locale: makeSelectLocale(),
});

export default connect(mapStateToProps)(injectIntl(App));
